import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getFirestoreInstance,
  getAuthInstance,
  waitForAuthBridge,
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
} from '@/lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import type { Firestore } from 'firebase/firestore';
import type { ChatMessage, GifData, TrackData } from './use-league-chat';

export type { ChatMessage, GifData, TrackData };

interface UseDailyRoundChatOptions {
  roundId: number;
  userId: string | null;
  username: string | null;
  avatarUrl: string | null;
  enabled?: boolean;
  messageLimit?: number;
}

interface SendMessageOptions {
  content: string;
  contentType?: 'text' | 'image' | 'gif' | 'track';
  imageUrl?: string;
  gifData?: GifData;
  trackData?: TrackData;
}

interface UseDailyRoundChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (options: SendMessageOptions) => Promise<void>;
  isSending: boolean;
}

/**
 * Hook to manage real-time chat messages for daily round lobby using Firestore.
 * This is a round-level chat (not room-scoped) for users before they're assigned to a room.
 * Uses dailyRoundChats/{roundId}/messages collection.
 */
export function useDailyRoundChat({
  roundId,
  userId,
  username,
  avatarUrl,
  enabled = true,
  messageLimit = 100,
}: UseDailyRoundChatOptions): UseDailyRoundChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const firestoreRef = useRef<Firestore | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!enabled || !roundId) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    let loadingTimeoutId: NodeJS.Timeout | null = null;

    loadingTimeoutId = setTimeout(() => {
      if (isMounted) {
        console.error('Lobby Chat: Global loading timeout reached (30s)');
        setError('Chat took too long to load. Please try again.');
        setIsLoading(false);
      }
    }, 30000);

    const setupListener = async () => {
      try {
        // On native platforms, wait for the auth bridge to complete first
        if (Capacitor.isNativePlatform()) {
          try {
            await Promise.race([
              waitForAuthBridge(),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Auth bridge timed out')), 15000)
              ),
            ]);
          } catch (bridgeError: any) {
            console.warn(`Lobby chat auth bridge failed: ${bridgeError?.message}`);
          }
        }

        const [firestore, auth] = await Promise.race([
          Promise.all([getFirestoreInstance(), getAuthInstance()]),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Firebase initialization timed out')), 10000)
          ),
        ]) as [Firestore | null, any];

        if (!firestore) {
          throw new Error('Firestore not initialized');
        }
        firestoreRef.current = firestore;

        // Wait for auth state
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(() => resolve(), 5000);
          const unsubAuth = onAuthStateChanged(auth, () => {
            clearTimeout(timeout);
            unsubAuth();
            resolve();
          }, () => {
            clearTimeout(timeout);
            resolve();
          });
        });

        // Use dailyRoundChats collection (round-level, not room-level)
        const messagesRef = collection(firestore, `dailyRoundChats/${roundId}/messages`);
        const messagesQuery = query(
          messagesRef,
          orderBy('createdAt', 'desc'),
          limit(messageLimit)
        );

        unsubscribeRef.current = onSnapshot(
          messagesQuery,
          (snapshot) => {
            if (!isMounted) return;

            const newMessages: ChatMessage[] = [];
            snapshot.forEach((doc) => {
              const data = doc.data();
              newMessages.push({
                id: doc.id,
                userId: data.userId,
                username: data.username,
                avatarUrl: data.avatarUrl,
                content: data.content,
                contentType: data.contentType || 'text',
                imageUrl: data.imageUrl || null,
                gifData: data.gifData || null,
                trackData: data.trackData || null,
                isDeleted: data.isDeleted || false,
                deletedBy: data.deletedBy || null,
                deletedAt: data.deletedAt?.toDate() || null,
                createdAt: data.createdAt?.toDate() || new Date(),
                replyToId: data.replyToId || null,
              });
            });

            setMessages(newMessages.reverse());
            setIsLoading(false);
            setError(null);
            if (loadingTimeoutId) {
              clearTimeout(loadingTimeoutId);
              loadingTimeoutId = null;
            }
          },
          (err) => {
            if (!isMounted) return;
            console.error('Lobby chat listener error:', err);
            if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('PERMISSION_DENIED')) {
              setError('Chat authentication failed. Please sign out and sign back in to access chat.');
            } else {
              setError('Failed to load chat messages');
            }
            setIsLoading(false);
            if (loadingTimeoutId) {
              clearTimeout(loadingTimeoutId);
              loadingTimeoutId = null;
            }
          }
        );
      } catch (err: any) {
        if (!isMounted) return;
        console.error('Lobby chat setup error:', err);
        if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('PERMISSION_DENIED')) {
          setError('Chat authentication failed. Please sign out and sign back in to access chat.');
        } else {
          setError('Failed to connect to chat');
        }
        setIsLoading(false);
        if (loadingTimeoutId) {
          clearTimeout(loadingTimeoutId);
          loadingTimeoutId = null;
        }
      }
    };

    setupListener();

    return () => {
      isMounted = false;
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
        loadingTimeoutId = null;
      }
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [roundId, enabled, messageLimit]);

  const sendMessage = useCallback(
    async (options: SendMessageOptions) => {
      const { content, contentType = 'text', imageUrl, gifData, trackData } = options;

      const needsContent = contentType === 'text';
      const hasRequiredContent = needsContent ? content.trim() : true;

      if (!userId || !username || !firestoreRef.current || !hasRequiredContent) {
        return;
      }

      setIsSending(true);
      setError(null);

      try {
        const auth = await getAuthInstance();
        const currentUser = auth?.currentUser;
        if (!currentUser) {
          throw new Error('Your session has expired. Please refresh the page and sign in again.');
        }

        const writeUserId = currentUser.uid;

        const messagesRef = collection(firestoreRef.current, `dailyRoundChats/${roundId}/messages`);

        await addDoc(messagesRef, {
          userId: writeUserId,
          username,
          avatarUrl,
          content: content.trim().slice(0, 500),
          contentType,
          imageUrl: imageUrl || null,
          gifData: gifData || null,
          trackData: trackData || null,
          isDeleted: false,
          deletedBy: null,
          deletedAt: null,
          createdAt: serverTimestamp(),
          replyToId: null,
        });
      } catch (err: any) {
        console.error('Send lobby message error:', err?.code, err?.message);

        if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('PERMISSION_DENIED')) {
          const auth = await getAuthInstance();
          if (!auth?.currentUser) {
            setError('Your session expired. Please refresh the page.');
            throw new Error('Session expired - please refresh the page and sign in again.');
          }
          setError('Unable to send message. Please try refreshing the page.');
          throw new Error('Chat permission denied. Please try refreshing the page.');
        }

        setError(err.message || 'Failed to send message');
        throw err;
      } finally {
        setIsSending(false);
      }
    },
    [roundId, userId, username, avatarUrl]
  );

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    isSending,
  };
}
